/**
 * API Client
 *
 * Type-safe Hono RPC client for communicating with the Cloudflare Worker API.
 * Provides methods for:
 * - Generating flashcards from images or text
 * - Fetching cards by ID
 * - Getting cards due for review
 * - Submitting card reviews
 *
 * Features:
 * - Automatic retry logic for failed requests
 * - Type-safe request/response handling
 * - FormData support for file uploads
 */

import { hc } from 'hono/client'
import type { GeneratedCard, CardWithSRS } from '@shared/schemas'
import { API_URL } from '@/env'
import { getAuthHeaders } from './auth'
import { getSessionToken } from './auth-client'

// Default base URL from environment configuration
// In single-domain mode (Worker Static Assets), API_URL is empty
// and requests go to the same origin
const DEFAULT_BASE_URL = API_URL || (typeof window !== 'undefined' ? window.location.origin : '')

// Maximum retry attempts for failed requests
const MAX_RETRIES = 0

// Retry delay in milliseconds
const RETRY_DELAY = 1000

/**
 * Validates if a string is a valid URL format
 */
function isValidUrl(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

/**
 * Sleep utility for retry delay
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Build auth headers with both device ID and session token (when available).
 * Used by all direct fetch functions to ensure Google-authenticated requests
 * resolve to the correct user ID instead of falling back to device-based auth.
 */
function buildAuthHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    ...extra,
    ...getAuthHeaders(), // X-Device-ID
  }
  const sessionToken = getSessionToken()
  if (sessionToken) {
    headers['X-Session-Token'] = sessionToken
  }
  return headers
}

/**
 * Wraps fetch with retry logic
 */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  retries = MAX_RETRIES
): Promise<Response> {
  try {
    const response = await fetch(url, options) as Response

    // Don't retry on 4xx errors (client errors)
    if (response.status >= 400 && response.status < 500) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }))
      throw new Error(errorData.message || `HTTP ${response.status}`)
    }

    // Retry on 5xx errors
    if (!response.ok && retries > 0) {
      await sleep(RETRY_DELAY)
      return fetchWithRetry(url, options, retries - 1)
    }

    // Throw on error if no retries left
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }))
      throw new Error(errorData.message || `HTTP ${response.status}`)
    }

    return response
  } catch (error) {
    // If it's a network error or timeout, retry
    const isNetworkError = error instanceof Error && (
      error.message.includes('fetch failed') ||
      error.message.includes('ECONNREFUSED') ||
      error.message.includes('ENOTFOUND') ||
      error.message.includes('Network error')
    )

    if (isNetworkError && retries > 0) {
      await sleep(RETRY_DELAY)
      return fetchWithRetry(url, options, retries - 1)
    }

    // Re-throw the error
    throw error
  }
}

/**
 * Creates a type-safe API client for the Multimodal Smart Lexicon API
 *
 * @param baseUrl - The base URL of the API (default: from env or localhost)
 * @returns A type-safe client with all API methods
 *
 * @example
 * ```ts
 * const client = createApiClient()
 * const result = await client.api.cards.generate.$post({
 *   form: { text: 'hello' }
 * })
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createApiClient(baseUrl: string = DEFAULT_BASE_URL): any {
  // Validate base URL
  if (!baseUrl || baseUrl.trim() === '') {
    throw new Error('Base URL is required')
  }

  if (!isValidUrl(baseUrl)) {
    throw new Error('Invalid base URL format')
  }

  // Custom fetch wrapper that adds auth headers and credentials
  const customFetch = async (url: string, options?: RequestInit) => {
    // Add development auth headers and session token
    const headers: Record<string, string> = {
      ...options?.headers as Record<string, string>,
      ...getAuthHeaders(),
    }
    const sessionToken = getSessionToken()
    if (sessionToken) {
      headers['X-Session-Token'] = sessionToken
    }

    return fetchWithRetry(url, {
      ...options,
      headers,
      credentials: 'include', // Send session cookies for Google auth
    } as RequestInit)
  }

  // Create Hono client with custom fetch
  const client = hc(baseUrl, {
    fetch: customFetch as typeof fetch
  })

  return client
}

/**
 * Direct fetch for FormData uploads
 * Workaround for Hono RPC client not handling FormData properly
 */
export async function generateCard(formData: FormData): Promise<{
  cards: GeneratedCard[]
  count: number
}> {
  const url = `${DEFAULT_BASE_URL}/api/cards/generate`

  const response = await fetchWithRetry(url, {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: formData,
    credentials: 'include',
  })

  return response.json()
}

/**
 * Save a generated card to the library
 */
export async function saveCard(
  card: Partial<GeneratedCard> & { imageKey?: string | null }
): Promise<{ success: boolean; id: string }> {
  const url = `${DEFAULT_BASE_URL}/api/cards`

  const headers: Record<string, string> = {
    ...buildAuthHeaders({ 'Content-Type': 'application/json' })
  }

  const response = await fetchWithRetry(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      front: card.front,
      back: card.back,
      definition: card.definition,
      example: card.example,
      pos: card.pos,
      type: card.type,
      imageKey: card.imageKey || null
    })
  })

  return response.json()
}

/**
 * Delete a card from the library
 */
export async function deleteCard(cardId: string): Promise<{ success: boolean; message: string }> {
  const url = `${DEFAULT_BASE_URL}/api/cards/${cardId}`

  const response = await fetchWithRetry(url, {
    method: 'DELETE',
    headers: buildAuthHeaders()
  })

  return response.json()
}

/**
 * Get cards from the library with page-based pagination
 */
export async function getAllCards(options?: { cursor?: string; limit?: number; search?: string; mastered?: boolean; page?: number }): Promise<{
  success: boolean
  cards: Array<{
    id: string
    user_id: string
    front: string
    back: string
    definition: string
    example: string
    pos: string
    phonetic_us: string
    phonetic_uk: string
    type: string
    image_key: string | null
    tags: string[]
    created_at: string
  }>
  count: number
  hasMore: boolean
  nextCursor?: string
  total: number
}> {
  const params = new URLSearchParams()
  if (options?.limit) params.set('limit', String(options.limit))
  if (options?.cursor) params.set('cursor', options.cursor)
  if (options?.search) params.set('search', options.search)
  if (options?.mastered !== undefined) params.set('mastered', String(options.mastered))
  if (options?.page) params.set('page', String(options.page))
  const qs = params.toString()
  const url = `${DEFAULT_BASE_URL}/api/cards${qs ? `?${qs}` : ''}`

  const response = await fetchWithRetry(url, {
    method: 'GET',
    headers: buildAuthHeaders()
  })

  return response.json()
}

/**
 * Update a card in the library
 */
export async function updateCard(
  cardId: string,
  cardData: {
    front?: string
    back?: string
    definition?: string
    example?: string
    pos?: string
    phonetic_us?: string
    phonetic_uk?: string
    type?: string
    imageKey?: string | null
  }
): Promise<{
  success: boolean
  card: {
    id: string
    front: string
    back: string
    definition: string
    example: string
    pos: string
    phonetic_us: string
    phonetic_uk: string
    type: string
    image_key: string | null
    created_at: string
  }
}> {
  const url = `${DEFAULT_BASE_URL}/api/cards/${cardId}`

  const headers: Record<string, string> = {
    ...buildAuthHeaders({ 'Content-Type': 'application/json' })
  }

  const response = await fetchWithRetry(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify(cardData)
  })

  return response.json()
}

/**
 * Type exports for convenience
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ApiClient = any

/**
 * Get cards due for review, optionally filtered by tags
 */
export async function getDueCards(limit = 20, direction: 'forward' | 'reverse' = 'forward', tags?: string[]): Promise<{
  cards: CardWithSRS[]
  resolvedTags?: string[]
  matchMode?: 'AND' | 'OR' | 'none'
}> {
  const params = new URLSearchParams({ limit: String(limit) })
  if (tags && tags.length > 0) {
    params.set('tags', tags.join(','))
  }
  if (direction === 'reverse') params.set('direction', 'reverse')
  const url = `${DEFAULT_BASE_URL}/api/cards/due?${params}`

  const response = await fetchWithRetry(url, {
    method: 'GET',
    headers: buildAuthHeaders()
  })

  return response.json()
}

/**
 * Get count of cards reviewed today
 */
export async function getReviewedTodayCount(): Promise<{
  success: boolean
  reviewedToday: number
}> {
  const tzOffset = new Date().getTimezoneOffset()
  const url = `${DEFAULT_BASE_URL}/api/cards/due/reviewed-today?tzOffset=${tzOffset}`

  const response = await fetchWithRetry(url, {
    method: 'GET',
    headers: buildAuthHeaders()
  })

  return response.json()
}

/**
 * Submit a card review with SRS rating
 */
export async function submitCardReview(
  cardId: string,
  rating: number,
  direction: 'forward' | 'reverse' = 'forward'
): Promise<{ success: boolean }> {
  const url = `${DEFAULT_BASE_URL}/api/cards/${cardId}/review`

  const headers: Record<string, string> = {
    ...buildAuthHeaders({ 'Content-Type': 'application/json' })
  }

  const response = await fetchWithRetry(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ rating, direction })
  })

  return response.json()
}

/**
 * Get tag cloud for user's cards — all unique tags grouped by category with counts
 */
export async function getTagCloud(direction?: 'forward' | 'reverse'): Promise<{
  success: boolean
  domains: Array<{ name: string; count: number }>
  topics: Array<{ name: string; count: number }>
  forms: Array<{ name: string; count: number }>
  registers: Array<{ name: string; count: number }>
  emotions: Array<{ name: string; count: number }>
  variants: Array<{ name: string; count: number }>
  confusables: Array<{ name: string; count: number }>
  grammar: Array<{ name: string; count: number }>
  wordFamilies: Array<{ name: string; count: number }>
}> {
  const params = new URLSearchParams()
  if (direction) params.set('direction', direction)
  const qs = params.toString()
  const url = `${DEFAULT_BASE_URL}/api/cards/tags${qs ? `?${qs}` : ''}`

  const response = await fetchWithRetry(url, {
    method: 'GET',
    headers: buildAuthHeaders()
  })

  return response.json()
}

/**
 * Mark or unmark a card as mastered.
 * Mastered cards are excluded from all review sessions.
 */
export async function setCardMastered(
  cardId: string,
  mastered: boolean
): Promise<{ success: boolean; mastered: boolean }> {
  const url = `${DEFAULT_BASE_URL}/api/cards/${cardId}/mastered`

  const response = await fetchWithRetry(url, {
    method: 'PUT',
    headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ mastered })
  })

  return response.json()
}

/**
 * Get all mastered cards with cursor-based pagination
 */
export async function getMasteredCards(options?: { cursor?: string; limit?: number; search?: string; page?: number }): Promise<{
  success: boolean
  cards: Array<{
    id: string
    user_id: string
    front: string
    back: string
    definition: string
    example: string
    pos: string
    phonetic_us: string
    phonetic_uk: string
    type: string
    image_key: string | null
    tags: string[]
    created_at: string
  }>
  count: number
  hasMore: boolean
  nextCursor?: string
  total: number
}> {
  const params = new URLSearchParams()
  if (options?.limit) params.set('limit', String(options.limit))
  if (options?.cursor) params.set('cursor', options.cursor)
  if (options?.search) params.set('search', options.search)
  if (options?.page) params.set('page', String(options.page))
  const qs = params.toString()
  const url = `${DEFAULT_BASE_URL}/api/cards/mastered${qs ? `?${qs}` : ''}`

  const response = await fetchWithRetry(url, {
    method: 'GET',
    headers: buildAuthHeaders()
  })

  return response.json()
}

export async function getAccumulatedStats(): Promise<{
  success: boolean
  reviewedToday: number
  totalReviews: number
  totalCards: number
  masteredCards: number
}> {
  const tzOffset = new Date().getTimezoneOffset()
  const response = await fetch(`${DEFAULT_BASE_URL}/api/cards/stats?tzOffset=${tzOffset}`, {
    credentials: 'include',
    headers: buildAuthHeaders(),
  })
  if (!response.ok) throw new Error('Failed to fetch stats')
  return response.json()
}
